/**
 * A list of portal sessions: device, where from, when. Used by My preferences →
 * Sessions (with per-row "Sign out") and the Team drawer's Sign-in tab (read-only).
 */
import React from 'react';
import { LogOut, Monitor, Smartphone } from 'lucide-react';
import { AdminSession, sessionEndsAt, sessionSubtitle } from './sessionsLogic';

interface Props {
  sessions: AdminSession[];
  maxHours: number;
  busy?: boolean;
  onEnd?: (s: AdminSession) => void;
  emptyText?: string;
}

const isPhone = (device: string) => /iPhone|Android|iPad/.test(device);

export const SessionsList: React.FC<Props> = ({ sessions, maxHours, busy, onEnd, emptyText }) => {
  if (!sessions.length) {
    return <p className="text-xs text-gray-500">{emptyText ?? 'No open sessions.'}</p>;
  }
  return (
    <ul className="divide-y divide-gray-100">
      {sessions.map(s => {
        const ends = sessionEndsAt(s, maxHours);
        const Icon = isPhone(s.device) ? Smartphone : Monitor;
        return (
          <li key={s.id} className="flex items-start justify-between gap-3 py-2 text-sm">
            <div className="flex min-w-0 items-start gap-2">
              <Icon className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
              <div className="min-w-0">
                <div className="font-medium text-gray-800">
                  {s.device}
                  {s.current && (
                    <span className="ml-2 rounded bg-green-50 px-1.5 py-0.5 text-[10px] font-medium text-green-700">
                      This device
                    </span>
                  )}
                </div>
                <div className="truncate text-xs text-gray-500">{sessionSubtitle(s)}</div>
                {ends && <div className="text-[11px] text-gray-400">Ends {ends.toLocaleString()}</div>}
              </div>
            </div>
            {onEnd && !s.current && (
              <button type="button" disabled={busy} onClick={() => onEnd(s)}
                aria-label={`Sign out ${s.device}`}
                className="inline-flex flex-shrink-0 items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                <LogOut className="h-3 w-3" /> Sign out
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
};
